var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
/* global window File Promise */
import React from "react";
import { EditorState, Selection } from "prosemirror-state";
import { dropCursor } from "prosemirror-dropcursor";
import { gapCursor } from "prosemirror-gapcursor";
import { EditorView } from "prosemirror-view";
import { Schema } from "prosemirror-model";
import { inputRules } from "prosemirror-inputrules";
import { keymap } from "prosemirror-keymap";
import { baseKeymap } from "prosemirror-commands";
import { selectColumn, selectRow, selectTable } from "prosemirror-utils";
import { styled, ThemeProvider } from "@reside/ui";
import { css } from "@emotion/react";
import Tooltip from "./components/Tooltip";
import ExtensionManager from "./lib/ExtensionManager";
import ComponentView from "./lib/ComponentView";
import headingToSlug from "./lib/headingToSlug";
import Doc from "./nodes/Doc";
import Text from "./nodes/Text";
import HardBreak from "./nodes/HardBreak";
import HorizontalRule from "./nodes/HorizontalRule";
import Paragraph from "./nodes/Paragraph";
import Answer from "./nodes/Answer";
// marks
import Bold from "./marks/Bold";
import Italic from "./marks/Italic";
import Strikethrough from "./marks/Strikethrough";
import TemplatePlaceholder from "./marks/Placeholder";
import Underline from "./marks/Underline";
import Highlight from "./marks/Highlight";
// plugins
import History from "./plugins/History";
import Keys from "./plugins/Keys";
import Placeholder from "./plugins/Placeholder";
import SmartText from "./plugins/SmartText";
import TrailingNode from "./plugins/TrailingNode";
import MarkdownPaste from "./plugins/MarkdownPaste";
import "prosemirror-menu/style/menu.css";
import "antd/dist/antd.css";
import { TopMenu } from "./components/TopMenu";
export { schema, parser, serializer } from "./server";
export { default as Extension } from "./lib/Extension";
var MarkdownEditor = /** @class */ (function (_super) {
    __extends(MarkdownEditor, _super);
    function MarkdownEditor() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            blockMenuOpen: false,
            linkMenuOpen: false,
            blockMenuSearch: "",
        };
        _this.value = function () {
            // @ts-ignore
            return _this.serializer.serialize(_this.view.state.doc);
        };
        _this.handleChange = function () {
            if (!_this.props.onChange)
                return;
            _this.props.onChange(function () {
                return _this.value();
            });
        };
        _this.handleSave = function () {
            var onSave = _this.props.onSave;
            if (onSave) {
                onSave({ done: false });
            }
        };
        _this.handleSaveAndExit = function () {
            var onSave = _this.props.onSave;
            if (onSave) {
                onSave({ done: true });
            }
        };
        _this.handleOpenLinkMenu = function () {
            _this.setState({ linkMenuOpen: true });
        };
        _this.handleCloseLinkMenu = function () {
            _this.setState({ linkMenuOpen: false });
        };
        _this.handleOpenBlockMenu = function (search) {
            _this.setState({ blockMenuOpen: true, blockMenuSearch: search });
        };
        _this.handleCloseBlockMenu = function () {
            if (!_this.state.blockMenuOpen)
                return;
            _this.setState({ blockMenuOpen: false });
        };
        _this.handleSelectRow = function (index, state) {
            // @ts-ignore
            _this.view.dispatch(selectRow(index)(state.tr));
        };
        _this.handleSelectColumn = function (index, state) {
            // @ts-ignore
            _this.view.dispatch(selectColumn(index)(state.tr));
        };
        _this.handleSelectTable = function (state) {
            // @ts-ignore
            _this.view.dispatch(selectTable(state.tr));
        };
        // 'public' methods
        _this.focusAtStart = function () {
            // @ts-ignore
            var selection = Selection.atStart(_this.view.state.doc);
            // @ts-ignore
            var transaction = _this.view.state.tr.setSelection(selection);
            // @ts-ignore
            _this.view.dispatch(transaction);
            // @ts-ignore
            _this.view.focus();
        };
        _this.focusAtEnd = function () {
            // @ts-ignore
            var selection = Selection.atEnd(_this.view.state.doc);
            // @ts-ignore
            var transaction = _this.view.state.tr.setSelection(selection);
            // @ts-ignore
            _this.view.dispatch(transaction);
            // @ts-ignore
            _this.view.focus();
        };
        _this.getHeadings = function () {
            var headings = [];
            var previouslySeen = {};
            // @ts-ignore
            _this.view.state.doc.forEach(function (node) {
                if (node.type.name === "heading") {
                    // calculate the optimal slug
                    var slug = headingToSlug(node);
                    var id = slug;
                    // check if we've already used it, and if so how many times?
                    // Make the new id based on that number ensuring that we have
                    // unique ID's even when headings are identical
                    // @ts-ignore
                    if (previouslySeen[slug] > 0) {
                        // @ts-ignore
                        id = headingToSlug(node, previouslySeen[slug]);
                    }
                    // record that we've seen this slug for the next loop
                    // @ts-ignore
                    previouslySeen[slug] =
                        // @ts-ignore
                        previouslySeen[slug] !== undefined ? previouslySeen[slug] + 1 : 1;
                    headings.push({
                        title: node.textContent,
                        level: node.attrs.level,
                        id: id,
                    });
                }
            });
            return headings;
        };
        _this.Editor = function (props) {
            var _a = _this.props, readOnly = _a.readOnly, readOnlyWriteCheckboxes = _a.readOnlyWriteCheckboxes;
            return (React.createElement(StyledEditor, __assign({}, props, { readOnly: readOnly, readOnlyWriteCheckboxes: readOnlyWriteCheckboxes, ref: function (ref) { return (_this.element = ref); } })));
        };
        _this.Menu = function (props) {
            var _a, _b, _c;
            var _d = _this.props, readOnly = _d.readOnly, hasTopMenu = _d.hasTopMenu;
            if (!readOnly &&
                hasTopMenu &&
                _this.view && ((_a = _this.view) === null || _a === void 0 ? void 0 : _a.state) &&
                _this.commands) {
                return (React.createElement(TopMenu, __assign({}, props, { state: (_b = _this.view) === null || _b === void 0 ? void 0 : _b.state, selection: (_c = _this.view) === null || _c === void 0 ? void 0 : _c.state.selection, answers: _this.props.answers, treeData: _this.props.treeData, commands: _this.commands, isTemplate: _this.props.template === true })));
            }
            return null;
        };
        _this.render = function () {
            var _a = _this.props, style = _a.style, className = _a.className, onKeyDown = _a.onKeyDown, children = _a.children;
            return (React.createElement(ThemeProvider, null,
                React.createElement(Flex, { onKeyDown: onKeyDown, style: style, className: className }, children({
                    Menu: _this.Menu,
                    Editor: _this.Editor,
                }))));
        };
        return _this;
    }
    MarkdownEditor.prototype.componentDidMount = function () {
        this.init();
        if (this.props.scrollTo) {
            this.scrollToAnchor(this.props.scrollTo);
        }
        if (this.props.readOnly)
            return;
        if (this.props.autoFocus) {
            this.focusAtEnd();
        }
    };
    MarkdownEditor.prototype.componentDidUpdate = function (prevProps) {
        var _this = this;
        // Allow changes to the 'value' prop to update the editor from outside
        if (this.props.value && prevProps.value !== this.props.value) {
            var newState = this.createState(this.props.value);
            // @ts-ignore
            this.view.updateState(newState);
        }
        // pass readOnly changes through to underlying editor instance
        if (prevProps.readOnly !== this.props.readOnly) {
            // @ts-ignore
            this.view.update(__assign(__assign({}, this.view.props), { editable: function () { return !_this.props.readOnly; } }));
        }
        if (this.props.scrollTo && this.props.scrollTo !== prevProps.scrollTo) {
            this.scrollToAnchor(this.props.scrollTo);
        }
        // Focus at the end of the document if switching from readOnly and autoFocus
        // is set to true
        if (prevProps.readOnly && !this.props.readOnly && this.props.autoFocus) {
            this.focusAtEnd();
        }
    };
    MarkdownEditor.prototype.init = function () {
        this.extensions = this.createExtensions();
        this.nodes = this.createNodes();
        this.marks = this.createMarks();
        this.schema = this.createSchema();
        this.plugins = this.createPlugins();
        this.keymaps = this.createKeymaps();
        this.serializer = this.createSerializer();
        this.parser = this.createParser();
        this.inputRules = this.createInputRules();
        this.nodeViews = this.createNodeViews();
        this.view = this.createView();
        this.commands = this.createCommands();
    };
    MarkdownEditor.prototype.createExtensions = function () {
        // adding nodes here? Update schema.ts for serialization on the server
        return new ExtensionManager(__spreadArrays([
            new Doc(),
            new Text(),
            new HardBreak(),
            new Paragraph(),
            new Answer(),
            new HorizontalRule(),
            new Bold(),
            new Italic(),
            new Highlight(),
            new TemplatePlaceholder(),
            new Underline(),
            new Strikethrough(),
            new History(),
            new SmartText(),
            new TrailingNode(),
            new MarkdownPaste(),
            new Keys({
                onSave: this.handleSave,
                onSaveAndExit: this.handleSaveAndExit,
                onCancel: this.props.onCancel,
            }),
            new Placeholder({
                placeholder: this.props.placeholder,
            })
        ], this.props.extensions), this);
    };
    MarkdownEditor.prototype.createPlugins = function () {
        // @ts-ignore
        return this.extensions.plugins;
    };
    MarkdownEditor.prototype.createKeymaps = function () {
        // @ts-ignore
        return this.extensions.keymaps({
            // @ts-ignore
            schema: this.schema,
        });
    };
    MarkdownEditor.prototype.createInputRules = function () {
        // @ts-ignore
        return this.extensions.inputRules({
            // @ts-ignore
            schema: this.schema,
        });
    };
    MarkdownEditor.prototype.createNodeViews = function () {
        var _this = this;
        return (
        // @ts-ignore
        this.extensions.extensions
            // @ts-ignore
            .filter(function (extension) { return extension.component; })
            // @ts-ignore
            .reduce(function (nodeViews, extension) {
            var _a;
            // @ts-ignore
            var nodeView = function (node, view, getPos, decorations) {
                return new ComponentView(extension.component, {
                    editor: _this,
                    extension: extension,
                    node: node,
                    view: view,
                    getPos: getPos,
                    decorations: decorations,
                });
            };
            return __assign(__assign({}, nodeViews), (_a = {}, _a[extension.name] = nodeView, _a));
        }, {}));
    };
    MarkdownEditor.prototype.createCommands = function () {
        // @ts-ignore
        return this.extensions.commands({
            schema: this.schema,
            view: this.view,
        });
    };
    MarkdownEditor.prototype.createNodes = function () {
        // @ts-ignore
        return this.extensions.nodes;
    };
    MarkdownEditor.prototype.createMarks = function () {
        // @ts-ignore
        return this.extensions.marks;
    };
    MarkdownEditor.prototype.createSchema = function () {
        return new Schema({
            // @ts-ignore
            nodes: this.nodes,
            marks: this.marks,
        });
    };
    MarkdownEditor.prototype.createSerializer = function () {
        // @ts-ignore
        return this.extensions.serializer();
    };
    MarkdownEditor.prototype.createParser = function () {
        // @ts-ignore
        return this.extensions.parser({
            schema: this.schema,
            answers: this.props.answers,
        });
    };
    MarkdownEditor.prototype.createState = function (value) {
        var doc = this.createDocument(value || this.props.defaultValue);
        return EditorState.create({
            schema: this.schema,
            doc: doc,
            plugins: __spreadArrays(this.plugins, this.keymaps, [
                dropCursor(),
                gapCursor(),
                inputRules({
                    // @ts-ignore
                    rules: this.inputRules,
                }),
                keymap(baseKeymap),
            ]),
        });
    };
    MarkdownEditor.prototype.createDocument = function (content) {
        // @ts-ignore
        return this.parser.parse(content);
    };
    MarkdownEditor.prototype.createView = function () {
        var _this = this;
        if (!this.element) {
            throw new Error("createView called before ref available");
        }
        // @ts-ignore
        var isEditingCheckbox = function (tr) {
            return tr.steps.some(function (step) {
                return step.slice.content.firstChild &&
                    step.slice.content.firstChild.type.name ===
                        // @ts-ignore
                        _this.schema.nodes.checkbox_item.name;
            });
        };
        var view = new EditorView(this.element, {
            state: this.createState(),
            editable: function () { return !_this.props.readOnly; },
            nodeViews: this.nodeViews,
            handleDOMEvents: this.props.handleDOMEvents,
            dispatchTransaction: function (transaction) {
                var _a;
                var value = ((_a = _this.serializer) === null || _a === void 0 ? void 0 : _a.serialize(transaction.doc)) || "";
                var hasNewlineBeginning = value.match(/^\\\s\S+/gm) || [];
                if (hasNewlineBeginning.length) {
                    return;
                }
                // @ts-ignore
                var _b = _this.view.state.applyTransaction(transaction), state = _b.state, transactions = _b.transactions;
                // @ts-ignore
                _this.view.updateState(state);
                // If any of the transactions being dispatched resulted in the doc
                // changing then call our own change handler to let the outside world
                // know
                if (transactions.some(function (tr) { return tr.docChanged; }) &&
                    (!_this.props.readOnly ||
                        (_this.props.readOnlyWriteCheckboxes &&
                            transactions.some(isEditingCheckbox)))) {
                    _this.handleChange();
                }
                // Because Prosemirror and React are not linked we must tell React that
                // a render is needed whenever the Prosemirror state changes.
                _this.forceUpdate();
            },
        });
        return view;
    };
    MarkdownEditor.prototype.scrollToAnchor = function (hash) {
        if (!hash)
            return;
        try {
            var element = document.querySelector(hash);
            if (element)
                element.scrollIntoView({ behavior: "smooth" });
        }
        catch (err) {
            // querySelector will throw an error if the hash begins with a number
            // or contains a period. This is protected against now by safeSlugify
            // however previous links may be in the wild.
            // eslint-disable-next-line no-console
            console.warn("Attempted to scroll to invalid hash: " + hash, err);
        }
    };
    MarkdownEditor.defaultProps = {
        defaultValue: "",
        hasTopMenu: true,
        placeholder: "Enter text…",
        onImageUploadStart: function () {
            // no default behavior
        },
        onImageUploadStop: function () {
            // no default behavior
        },
        onClickLink: function (href) {
            window.open(href, "_blank");
        },
        embeds: [],
        extensions: [],
        tooltip: Tooltip,
        answers: {},
        children: function (_a) {
            var Menu = _a.Menu, Editor = _a.Editor;
            return (React.createElement(React.Fragment, null,
                React.createElement(Menu, null),
                React.createElement(Editor, null)));
        },
    };
    return MarkdownEditor;
}(React.PureComponent));
var Flex = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  align-items: flex-start;\n  justify-content: center;\n"], ["\n  display: flex;\n  flex-direction: column;\n  align-items: flex-start;\n  justify-content: center;\n"])));
var StyledEditor = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  width: 100%;\n\n  .ant-tag {\n    line-height: inherit;\n    font-size: inherit;\n    margin-right: 0.1em;\n  }\n\n  .ProseMirror {\n    position: relative;\n    outline: none;\n    word-wrap: break-word;\n    white-space: break-spaces;\n    -webkit-font-variant-ligatures: none;\n    font-variant-ligatures: none;\n    font-feature-settings: \"liga\" 0; /* the above doesn't seem to work in Edge */\n  }\n\n  .ProseMirror-hideselection *::selection {\n    background: transparent;\n  }\n  .ProseMirror-hideselection *::-moz-selection {\n    background: transparent;\n  }\n  .ProseMirror-hideselection {\n    caret-color: transparent;\n  }\n\n  .ProseMirror-selectednode {\n    outline: 1px solid transparent;\n\n    ", "\n  }\n\n  .scrollable {\n    overflow-y: hidden;\n    overflow-x: auto;\n    padding-left: 1em;\n    margin-left: -1em;\n    border-left: 1px solid transparent;\n    border-right: 1px solid transparent;\n    transition: border 250ms ease-in-out 0s;\n  }\n\n  .scrollable-shadow {\n    position: absolute;\n    top: 0;\n    bottom: 0;\n    left: -1em;\n    width: 16px;\n    transition: box-shadow 250ms ease-in-out;\n    border: 0px solid transparent;\n    border-left-width: 1em;\n    pointer-events: none;\n\n    &.left {\n      box-shadow: 16px 0 16px -16px inset rgba(0, 0, 0, 0.25);\n      border-left: 1em solid ", ";\n    }\n\n    &.right {\n      right: 0;\n      left: auto;\n      box-shadow: -16px 0 16px -16px inset rgba(0, 0, 0, 0.25);\n    }\n  }\n\n  @media print {\n    .block-menu-trigger {\n      display: none;\n    }\n  }\n\n  .ProseMirror-gapcursor {\n    display: none;\n    pointer-events: none;\n    position: absolute;\n  }\n\n  .ProseMirror-gapcursor:after {\n    content: \"\";\n    display: block;\n    position: absolute;\n    top: -2px;\n    width: 20px;\n    border-top: 1px solid ", ";\n    animation: ProseMirror-cursor-blink 1.1s steps(2, start) infinite;\n  }\n\n  @keyframes ProseMirror-cursor-blink {\n    to {\n      visibility: hidden;\n    }\n  }\n\n  .ProseMirror-focused .ProseMirror-gapcursor {\n    display: block;\n  }\n\n  .placeholder {\n    &:before {\n      display: block;\n      content: ", ";\n      pointer-events: none;\n      height: 0;\n      opacity: 0.4;\n      filter: saturate(25%);\n    }\n  }\n"], ["\n  width: 100%;\n\n  .ant-tag {\n    line-height: inherit;\n    font-size: inherit;\n    margin-right: 0.1em;\n  }\n\n  .ProseMirror {\n    position: relative;\n    outline: none;\n    word-wrap: break-word;\n    white-space: break-spaces;\n    -webkit-font-variant-ligatures: none;\n    font-variant-ligatures: none;\n    font-feature-settings: \"liga\" 0; /* the above doesn't seem to work in Edge */\n  }\n\n  .ProseMirror-hideselection *::selection {\n    background: transparent;\n  }\n  .ProseMirror-hideselection *::-moz-selection {\n    background: transparent;\n  }\n  .ProseMirror-hideselection {\n    caret-color: transparent;\n  }\n\n  .ProseMirror-selectednode {\n    outline: 1px solid transparent;\n\n    ",
    "\n  }\n\n  .scrollable {\n    overflow-y: hidden;\n    overflow-x: auto;\n    padding-left: 1em;\n    margin-left: -1em;\n    border-left: 1px solid transparent;\n    border-right: 1px solid transparent;\n    transition: border 250ms ease-in-out 0s;\n  }\n\n  .scrollable-shadow {\n    position: absolute;\n    top: 0;\n    bottom: 0;\n    left: -1em;\n    width: 16px;\n    transition: box-shadow 250ms ease-in-out;\n    border: 0px solid transparent;\n    border-left-width: 1em;\n    pointer-events: none;\n\n    &.left {\n      box-shadow: 16px 0 16px -16px inset rgba(0, 0, 0, 0.25);\n      border-left: 1em solid ", ";\n    }\n\n    &.right {\n      right: 0;\n      left: auto;\n      box-shadow: -16px 0 16px -16px inset rgba(0, 0, 0, 0.25);\n    }\n  }\n\n  @media print {\n    .block-menu-trigger {\n      display: none;\n    }\n  }\n\n  .ProseMirror-gapcursor {\n    display: none;\n    pointer-events: none;\n    position: absolute;\n  }\n\n  .ProseMirror-gapcursor:after {\n    content: \"\";\n    display: block;\n    position: absolute;\n    top: -2px;\n    width: 20px;\n    border-top: 1px solid ", ";\n    animation: ProseMirror-cursor-blink 1.1s steps(2, start) infinite;\n  }\n\n  @keyframes ProseMirror-cursor-blink {\n    to {\n      visibility: hidden;\n    }\n  }\n\n  .ProseMirror-focused .ProseMirror-gapcursor {\n    display: block;\n  }\n\n  .placeholder {\n    &:before {\n      display: block;\n      content: ", ";\n      pointer-events: none;\n      height: 0;\n      opacity: 0.4;\n      filter: saturate(25%);\n    }\n  }\n"])), function (_a) {
    var readOnly = _a.readOnly;
    return !readOnly && css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n        outline-color: rgb(24, 144, 255);\n        filter: hue-rotate(45deg);\n      "], ["\n        outline-color: rgb(24, 144, 255);\n        filter: hue-rotate(45deg);\n      "])));
}, function (_a) {
    var theme = _a.theme;
    return theme.color.borderGray;
}, function (_a) {
    var theme = _a.theme;
    return theme.color.borderGray;
}, function (props) { return (props.readOnly ? "" : "attr(data-empty-text)"); });
export default MarkdownEditor;
var templateObject_1, templateObject_2, templateObject_3;
